import { useEffect, useRef, useState } from 'react'
import { Box, Flex, Text, VStack } from '@chakra-ui/react'
import { AnimatePresence, motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { LuBuilding, LuCheck, LuCircleHelp, LuLanguages, LuLogOut, LuSettings, LuShield, LuUser } from 'react-icons/lu'
import { useAuth } from '../../contexts/AuthContext'
import { usePermissions } from '../../auth/PermissionContext'
import { useT } from '../../i18n'
import { tenantsService, type TenantResponse } from '../../api/services/tenants.service'
import { authService } from '../../api/services/auth.service'
import { fonts, mineral, shadows } from '../../theme'

function getInitials(fullName: string): string {
  const parts = fullName.trim().split(/\s+/)
  if (parts.length >= 2) return (parts[0]![0]! + parts[1]![0]!).toUpperCase()
  return (parts[0]?.[0] ?? '').toUpperCase()
}

/** Hue estable por nombre: cada usuario tiene "su" mineral en el avatar. */
function avatarColor(name: string) {
  const palette = [mineral.ocreDeep, mineral.bosque600, mineral.lapis, mineral.cobre, mineral.turquesa, mineral.rodocrosita, mineral.amatista, mineral.oroDeep]
  let h = 0
  for (const ch of name) h = (h * 31 + ch.charCodeAt(0)) >>> 0
  return palette[h % palette.length]!
}

function SectionTitle({ icon: Icon, children }: { icon: typeof LuUser; children: string }) {
  return (
    <Flex align="center" gap={2} px={4} pt={3} pb={1.5}>
      <Icon size={12} color={mineral.ink4} />
      <Text fontFamily={fonts.mono} fontSize="10px" color={mineral.ink3} textTransform="uppercase" letterSpacing="0.12em">{children}</Text>
    </Flex>
  )
}

/**
 * Menú de usuario: organización, rol, idioma y acciones. La lógica (cambio de
 * tenant/rol con recarga, carga perezosa de organizaciones con permiso
 * `tenants.list`) es la misma de la cabecera anterior; sólo cambia la forma.
 */
export default function UserMenu() {
  const navigate = useNavigate()
  const { user, logout, switchTenant } = useAuth()
  const { t, lang, changeLang } = useT()
  const { me, hasPermission } = usePermissions()

  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const [tenants, setTenants] = useState<TenantResponse[] | null>(null)
  const tenantsInFlight = useRef(false)
  const [switchingTenant, setSwitchingTenant] = useState(false)
  const [switchingRole, setSwitchingRole] = useState(false)

  useEffect(() => {
    function onDown(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [])

  // Selector de organización OCULTO por ahora: se opera solo sobre Kuntur (la plataforma)
  // y las demás organizaciones de prueba no deben verse. Para reactivarlo, poner true.
  const showTenantSwitcher = false
  const canListTenants = showTenantSwitcher && !!user?.isPlatform && hasPermission('tenants.list')
  useEffect(() => {
    if (!open || !canListTenants) return
    if (tenants !== null || tenantsInFlight.current) return
    tenantsInFlight.current = true
    tenantsService.list().then(setTenants).catch(() => setTenants([])).finally(() => { tenantsInFlight.current = false })
  }, [open, canListTenants, tenants])

  const handleSwitchTenant = async (tenantId: string) => {
    if (!user || tenantId === user.tenantId || switchingTenant) return
    setSwitchingTenant(true)
    const result = await switchTenant(tenantId)
    setSwitchingTenant(false)
    setOpen(false)
    if (result.success) window.location.reload()
  }

  const handleSwitchRole = async (roleId: string) => {
    if (!me || roleId === me.activeRoleId || switchingRole) return
    setSwitchingRole(true)
    try {
      const data = await authService.switchRole(roleId)
      localStorage.setItem('auth_token', data.accessToken)
      localStorage.setItem('refresh_token', data.refreshToken)
      localStorage.setItem('tenant_id', data.tenantId)
      localStorage.setItem('auth_user', JSON.stringify({
        userId: data.userId,
        username: data.username,
        fullName: data.fullName,
        roles: data.roles,
        tenantId: data.tenantId,
        tenantCode: data.tenantCode,
        tenantName: data.tenantName,
        isPlatform: data.isPlatform,
      }))
      window.location.reload()
    } catch {
      setSwitchingRole(false)
    }
  }

  const displayName = user?.fullName ?? 'Usuario'
  const initials = getInitials(displayName)
  const color = avatarColor(displayName)
  const activeRole = me?.roles.find(r => r.id === me.activeRoleId)

  const actions = [
    { icon: LuUser, label: t('userMenu.editProfile'), action: () => console.log('Edit profile') },
    { icon: LuSettings, label: t('userMenu.miEmpresa'), action: () => navigate('/app/settings') },
    { icon: LuCircleHelp, label: t('userMenu.help'), action: () => console.log('Help') },
  ]

  const optionRow = (isActive: boolean, busy: boolean) => ({
    as: 'button' as const,
    width: '100%',
    alignItems: 'center',
    gap: 2.5,
    px: 4,
    py: 2,
    cursor: busy ? 'wait' : 'pointer',
    opacity: busy && !isActive ? 0.5 : 1,
    color: isActive ? mineral.ocreDark : mineral.ink2,
    fontWeight: isActive ? 600 : 400,
    bg: isActive ? mineral.ocreSoft : 'transparent',
    _hover: isActive ? {} : { bg: mineral.sunk },
    transition: 'all .15s',
  })

  return (
    <Box ref={ref} position="relative">
      <Flex
        as="button"
        {...{ type: 'button' }}
        align="center"
        gap={2.5}
        pl={1}
        pr={{ base: 1, md: 3 }}
        py={1}
        borderRadius="full"
        bg={open ? mineral.surface : 'transparent'}
        border={`1px solid ${open ? mineral.line : 'transparent'}`}
        _hover={{ bg: mineral.surface, borderColor: mineral.line }}
        transition="all .15s"
        cursor="pointer"
        onClick={() => setOpen(o => !o)}
      >
        <Flex w="34px" h="34px" borderRadius="full" align="center" justify="center" bg={color} color="white"
          fontFamily={fonts.display} fontWeight={700} fontSize="13px" boxShadow={`0 0 0 2px ${mineral.canvas}, 0 0 0 3.5px ${color}55`}>
          {initials}
        </Flex>
        <Box display={{ base: 'none', md: 'block' }} textAlign="left" lineHeight="1.15">
          <Text fontSize="13px" fontWeight={600} color={mineral.ink} lineClamp={1} maxW="160px">{displayName}</Text>
          <Text fontSize="11px" color={mineral.ink3} lineClamp={1} maxW="160px">{activeRole?.name ?? user?.tenantCode}</Text>
        </Box>
      </Flex>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.97 }}
            transition={{ type: 'spring', stiffness: 520, damping: 34 }}
            style={{ position: 'absolute', top: 'calc(100% + 10px)', right: 0, minWidth: 280, zIndex: 100, transformOrigin: 'top right' }}
          >
            <Box bg={mineral.surface} border={`1px solid ${mineral.line}`} borderRadius="16px" overflow="hidden" boxShadow={shadows.lg} maxH="80vh" overflowY="auto">
              <Flex gap={3} align="center" p={4} borderBottom={`1px solid ${mineral.line}`}
                style={{ background: `linear-gradient(135deg, ${color}14, transparent 70%)` }}>
                <Flex w="42px" h="42px" borderRadius="12px" align="center" justify="center" bg={color} color="white" fontFamily={fonts.display} fontWeight={700}>{initials}</Flex>
                <Box minW={0}>
                  <Text fontWeight={700} fontSize="14px" color={mineral.ink} lineClamp={1}>{displayName}</Text>
                  <Text fontSize="12px" color={mineral.ink3} lineClamp={1}>{user?.username}</Text>
                </Box>
              </Flex>

              {canListTenants && (
                <Box borderBottom={`1px solid ${mineral.line}`}>
                  <SectionTitle icon={LuBuilding}>{t('userMenu.organization')}</SectionTitle>
                  <VStack gap={0} pb={2} alignItems="stretch" maxH="200px" overflowY="auto">
                    {tenants === null ? (
                      <Text fontSize="xs" color={mineral.ink4} px={4} py={2}>{t('common.loading')}…</Text>
                    ) : tenants.length === 0 ? (
                      <Text fontSize="xs" color={mineral.ink4} px={4} py={2}>{user?.tenantCode}</Text>
                    ) : tenants.map(tn => {
                      const isActive = tn.id === user?.tenantId
                      return (
                        <Flex key={tn.id} {...{ type: 'button' }} {...optionRow(isActive, switchingTenant)} onClick={() => handleSwitchTenant(tn.id)}>
                          <Box flex="1" textAlign="left">
                            <Text fontSize="sm">{tn.name}</Text>
                            <Text fontSize="2xs" color={mineral.ink4}>{tn.code}{tn.isPlatform ? ' · platform' : ''}</Text>
                          </Box>
                          {isActive && <LuCheck size={14} />}
                        </Flex>
                      )
                    })}
                  </VStack>
                </Box>
              )}

              {me && me.roles.length > 1 && (
                <Box borderBottom={`1px solid ${mineral.line}`}>
                  <SectionTitle icon={LuShield}>{t('userMenu.role')}</SectionTitle>
                  <VStack gap={0} pb={2} alignItems="stretch" maxH="200px" overflowY="auto">
                    {me.roles.map(r => {
                      const isActive = r.id === me.activeRoleId
                      return (
                        <Flex key={r.id} {...{ type: 'button' }} {...optionRow(isActive, switchingRole)} onClick={() => handleSwitchRole(r.id)}>
                          <Box flex="1" textAlign="left">
                            <Text fontSize="sm">{r.name}</Text>
                            <Text fontSize="2xs" color={mineral.ink4}>{r.code}{r.isExternal ? ' · external' : ''}</Text>
                          </Box>
                          {isActive && <LuCheck size={14} />}
                        </Flex>
                      )
                    })}
                  </VStack>
                </Box>
              )}

              <Box borderBottom={`1px solid ${mineral.line}`} pb={3}>
                <SectionTitle icon={LuLanguages}>{t('userMenu.language')}</SectionTitle>
                <Flex mx={4} p={1} gap={1} borderRadius="10px" bg={mineral.sunk} position="relative">
                  {([{ code: 'es', label: 'Español' }, { code: 'en', label: 'English' }, { code: 'qu', label: 'Runasimi' }] as const).map(l => {
                    const isActive = lang === l.code
                    return (
                      <Box key={l.code} as="button" {...{ type: 'button' }} flex="1" py={1.5} borderRadius="8px" cursor="pointer" position="relative"
                        color={isActive ? mineral.ink : mineral.ink3} fontSize="12px" fontWeight={600} onClick={() => changeLang(l.code)}>
                        {isActive && (
                          <motion.span layoutId="lang-pill" transition={{ type: 'spring', stiffness: 600, damping: 40 }}
                            style={{ position: 'absolute', inset: 0, borderRadius: 8, background: mineral.surface, boxShadow: shadows.sm }} />
                        )}
                        <Text as="span" position="relative">{l.label}</Text>
                      </Box>
                    )
                  })}
                </Flex>
              </Box>

              <VStack gap={0} py={1.5} alignItems="stretch">
                {actions.map(a => (
                  <Flex key={a.label} as="button" {...{ type: 'button' }} align="center" gap={3} px={4} py={2.5} color={mineral.ink2} cursor="pointer"
                    _hover={{ bg: mineral.sunk, color: mineral.ink }} transition="all .15s" onClick={() => { a.action(); setOpen(false) }}>
                    <a.icon size={17} />
                    <Text fontSize="sm">{a.label}</Text>
                  </Flex>
                ))}
                <Flex as="button" {...{ type: 'button' }} align="center" gap={3} px={4} py={2.5} color={mineral.aji} cursor="pointer"
                  _hover={{ bg: mineral.ajiSoft }} transition="all .15s" onClick={() => { setOpen(false); logout() }}>
                  <LuLogOut size={17} />
                  <Text fontSize="sm" fontWeight={600}>{t('userMenu.logout')}</Text>
                </Flex>
              </VStack>
            </Box>
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  )
}
